import { Component, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSliderChange } from '@angular/material/slider';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil } from 'rxjs';

import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';

import { VideoEffectService } from '../services/video-effect.service';
import { socialAppSelectors, VideoEffect, VideoEffectsMode } from '../state/app';

const EFFECTS_DISABLED: VideoEffect = {
  enabled: false,
  mode: VideoEffectsMode.NONE,
};

@Component({
  selector: 'modal-video-effects',
  templateUrl: './modal-video-effects.component.html',
  styleUrls: ['./modal-video-effects.component.scss'],
  standalone: false,
})
export class ModalVideoEffectsComponent extends Loggable implements OnDestroy {
  public previewEffects = EFFECTS_DISABLED;
  public VideoEffectsMode = VideoEffectsMode;

  public backgroundImages = [
    { url: 'assets/images/video-backgrounds/abstract-blue-squares.jpg', text: 'Abstract blue squares' },
    { url: 'assets/images/video-backgrounds/abstract-blue-waves.jpg', text: 'Abstract blue waves' },
    { url: 'assets/images/video-backgrounds/abstract-hexagons.jpg', text: 'Abstract hexagons' },
    { url: 'assets/images/video-backgrounds/abstract-white.jpg', text: 'Abstract white space' },
    { url: 'assets/images/video-backgrounds/bookshelf-with-indoor-plants.jpg', text: 'Bookshelf with interior plants' },
    { url: 'assets/images/video-backgrounds/colorful-outer-space.jpg', text: 'Colorful outer space' },
    { url: 'assets/images/video-backgrounds/dallas-skyline-daytime.jpg', text: 'Dallas skyline daytime' },
    { url: 'assets/images/video-backgrounds/dallas-skyline-night.jpg', text: 'Dallas skyline night' },
    { url: 'assets/images/video-backgrounds/dark-blue-texture.jpg', text: 'Dark blue texture' },
    { url: 'assets/images/video-backgrounds/empty-office-interior-white.jpg', text: 'White empty office interior' },
    { url: 'assets/images/video-backgrounds/empty-office-interior-wood.jpg', text: 'Wood empty office interior' },
    { url: 'assets/images/video-backgrounds/geometric-black.jpg', text: 'Black geometric shapes' },
    { url: 'assets/images/video-backgrounds/glass-building-exterior.jpg', text: 'Glass building exterior' },
    { url: 'assets/images/video-backgrounds/home-interior.jpg', text: 'Home interior' },
    {
      url: 'assets/images/video-backgrounds/interior-with-armchair-and-plant.jpg',
      text: 'Interior with armchair and plant',
    },
    { url: 'assets/images/video-backgrounds/marble-black.jpg', text: 'Black marble' },
    { url: 'assets/images/video-backgrounds/marble-white.jpg', text: 'White marble' },
    { url: 'assets/images/video-backgrounds/minimalist-living-space.jpg', text: 'Minimalist living space' },
    { url: 'assets/images/video-backgrounds/modern-conference-room-2.jpg', text: 'Modern conference room' },
    { url: 'assets/images/video-backgrounds/modern-conference-room.jpg', text: 'Modern conference room' },
    {
      url: 'assets/images/video-backgrounds/modern-office-interior-with-desks.jpg',
      text: 'Modern office interior with desks',
    },
    { url: 'assets/images/video-backgrounds/modern-office-interior.jpg', text: 'Modern office interior' },
    { url: 'assets/images/video-backgrounds/modern-office-space.jpg', text: 'Modern office space' },
    {
      url: 'assets/images/video-backgrounds/neutral-background-with-home-decor-vase.jpg',
      text: 'Neutral background with home decor vase',
    },
    { url: 'assets/images/video-backgrounds/new-york-city-skyline-daytime.jpg', text: 'New York City Skyline' },
    { url: 'assets/images/video-backgrounds/office-building-exterior.jpg', text: 'Office building exterior' },
    { url: 'assets/images/video-backgrounds/office-interior-white.jpg', text: 'White office interior' },
    { url: 'assets/images/video-backgrounds/office-window-with-city-views.jpg', text: 'Office window with city views' },
    {
      url: 'assets/images/video-backgrounds/office-window-with-garden-view.jpg',
      text: 'Office window with garden view',
    },
    { url: 'assets/images/video-backgrounds/pastel-gradient.jpg', text: 'Pastel gradient' },
    { url: 'assets/images/video-backgrounds/preciate-macro-mural-neutral.jpg', text: 'Neutral Scoot mural' },
    { url: 'assets/images/video-backgrounds/preciate-mural-navy.jpg', text: 'Navy Scoot mural' },
    { url: 'assets/images/video-backgrounds/preciate-mural-neutral.jpg', text: 'Neutral Scoot mural' },
    { url: 'assets/images/video-backgrounds/preciate-shapes-cream.jpg', text: 'Cream Scoot shapes' },
    { url: 'assets/images/video-backgrounds/preciate-shapes-navy.jpg', text: 'Navy Scoot shapes' },
    { url: 'assets/images/video-backgrounds/shelf-with-books-and-plants.jpg', text: 'Shelf with books and plants' },
    { url: 'assets/images/video-backgrounds/shelves-with-many-books.jpg', text: 'Shelves with many books' },
    { url: 'assets/images/video-backgrounds/small-modern-conference-room.jpg', text: 'Small modern conference room' },
    { url: 'assets/images/video-backgrounds/snowy-window-views.jpg', text: 'Snowy window views' },
    { url: 'assets/images/video-backgrounds/stars-in-outer-space.jpg', text: 'Stars in outer space' },
    { url: 'assets/images/video-backgrounds/textured-wood-brown.jpg', text: 'Brown textured wood' },
    {
      url: 'assets/images/video-backgrounds/view-outside-home-office-window.jpg',
      text: 'View outside home office window',
    },
    { url: 'assets/images/video-backgrounds/wall-of-vines-with-wood.jpg', text: 'Wall of vines with wood' },
    { url: 'assets/images/video-backgrounds/watercolor-green.jpg', text: 'Green Watercolor' },
    {
      url: 'assets/images/video-backgrounds/white-brick-wall-with-bookcase-and-plants.jpg',
      text: 'White brick wall with bookcase and plants',
    },
    { url: 'assets/images/video-backgrounds/white-gray-abstract.jpg', text: 'White gray abstract background' },
    { url: 'assets/images/video-backgrounds/white-office-space-with-desks.jpg', text: 'White office space with desks' },
    { url: 'assets/images/video-backgrounds/window-views-greenery.jpg', text: 'Window views with greenery' },
  ];

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(
    public dialogRef: MatDialogRef<ModalVideoEffectsComponent>,
    private videoEffectService: VideoEffectService,
    private store: Store<AppState>,
  ) {
    super();
    this.setDisplayName('ModalVideoEffectsComponent');
    this.store
      .select(socialAppSelectors.getVideoEffect)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((effects) => (this.previewEffects = { ...effects }));
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public handleNoEffectsClick() {
    this.logUserAction('no effects', 'click');
    this.previewEffects = EFFECTS_DISABLED;
  }

  public handleBlurClick(blurRadius: number) {
    this.logUserAction('blur', 'click', { blurRadius });
    this.previewEffects = {
      enabled: true,
      mode: VideoEffectsMode.BLUR,
      blurRadius,
    };
  }

  public handleImageBackgroundModeClick() {
    this.logUserAction('background image', 'click');
    this.previewEffects = {
      enabled: true,
      mode: VideoEffectsMode.BACKGROUND_IMAGE,
      image: this.backgroundImages[0].url,
    };
  }

  public get imageBackgroundSelected(): boolean {
    return this.previewEffects.mode === VideoEffectsMode.BACKGROUND_IMAGE;
  }

  public get blurSelected(): boolean {
    return this.previewEffects.mode === VideoEffectsMode.BLUR;
  }

  public handleApply(): void {
    this.logUserAction('apply', 'click');
    this.videoEffectService.applyEffect(this.previewEffects);
    this.dialogRef.close();
  }

  public handleCancel(): void {
    this.logUserAction('cancel', 'click');
    this.dialogRef.close();
  }

  public onBlurChange(change: MatSliderChange) {
    this.blurRadius = change.value ?? 3;
  }

  public get blurRadius(): number {
    if (this.previewEffects.mode !== VideoEffectsMode.BLUR) {
      return 0;
    }
    return this.previewEffects.blurRadius ?? 3;
  }

  public set blurRadius(value: number) {
    this.previewEffects = { ...this.previewEffects, mode: VideoEffectsMode.BLUR, blurRadius: value ?? 3 };
  }

  public get mode(): VideoEffectsMode {
    return this.previewEffects.mode;
  }

  public get backgroundImage(): string | undefined {
    if (this.previewEffects.mode !== VideoEffectsMode.BACKGROUND_IMAGE) {
      return undefined;
    }
    return this.previewEffects.image;
  }

  public handleBackgroundImageClick(image: string) {
    this.logUserAction('background image', 'click', { image });
    this.previewEffects = { ...this.previewEffects, mode: VideoEffectsMode.BACKGROUND_IMAGE, image };
  }
}
